import React from 'react';
import { MIcon } from '../../lib/micon';
import { LdAuswahl, LdDatum, LdUmschalter } from './LdFenster';

/**
 * LehrerDesk — wiederverwendbare Formular-Primitives für Eingabe-Sheets (LdSheet/vaul).
 * Rein präsentational & controlled (Werte/Handler kommen von außen). Keine Business-Logik.
 * Welle K4: dieselbe Optik wie LdFenster (ein Feld-Stil für alle Fenster) — Auswahl und Datum
 * ohne native Felder, Kopf mit rundem Schließen-Knopf.
 */

/** Sheet-Kopf: Icon-Chip + Titel (+ Kontextzeile) + runder Schließen-Knopf. */
export function LdSheetHeader({ icon, title, subtitle, onClose }) {
  return (
    <div className="ld-fe-kopf ld-fe-kopf--innen">
      {icon != null && <span className="ld-fe-kopf__icon" aria-hidden="true">{icon}</span>}
      <div className="ld-fe-kopf__text">
        <div className="ld-fe-kopf__titel">{title}</div>
        {subtitle != null && <p className="ld-fe-kopf__kontext">{subtitle}</p>}
      </div>
      {onClose && (
        <button type="button" aria-label="Schließen" onClick={onClose} className="ld-fe-zu"><MIcon name="close" size={20} /></button>
      )}
    </div>
  );
}

/** Label (oben) + Control-Slot + optional Helper/Error. */
export function LdField({ label, helper, error, children }) {
  return (
    <div className="ld-fe-feld">
      {label != null && <div className="ld-fe-label"><label>{label}</label></div>}
      {children}
      {error != null ? <p className="ld-fe-fehler">{error}</p> : helper != null ? <p className="ld-fe-hinweis">{helper}</p> : null}
    </div>
  );
}

/** Auswahl statt <select> (children = <option>s, onChange bekommt { target: { value } }). */
export function LdSelectField({ value, onChange, children, icon: _icon, id, disabled, ...rest }) {
  return (
    <LdAuswahl id={id} value={value} onChange={onChange} disabled={disabled} aria-label={rest['aria-label']}>{children}</LdAuswahl>
  );
}

/** Weißes Eingabefeld; type="date" wird zum LdDatum (ohne natives Datumsfeld). */
export function LdInputField({ icon, type, ...rest }) {
  if (type === 'date') {
    return <LdDatum id={rest.id} value={rest.value} onChange={rest.onChange} min={rest.min} max={rest.max} disabled={rest.disabled} aria-label={rest['aria-label']} />;
  }
  return (
    <span className="ld-fe-huelle">
      {icon != null && <span className="ld-fe-vorne" aria-hidden="true">{icon}</span>}
      <input type={type} className={`ld-fe-input${icon != null ? ' mit-vorne' : ''}`} {...rest} />
    </span>
  );
}

/** Umbrechende Chip-Gruppe. */
export function LdChipGroup({ children }) {
  return <div className="ld-fe-chips">{children}</div>;
}

/** Einzelner Chip. active = Tint + Primärrahmen; color = Kategorie-Farbe als kleiner Punkt. */
export function LdChip({ active = false, onClick, children, color, ...rest }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={`ld-fe-chip${active ? ' ist-an' : ''}`} {...rest}>
      {color && <i style={{ background: color }} aria-hidden="true" />}{children}
    </button>
  );
}

/** 6 Noten-Knöpfe; gewählte gefüllt in farbeFn(note). */
export function LdGradePicker({ value, onChange, grades = [1, 2, 3, 4, 5, 6], farbeFn }) {
  return (
    <div className="ld-fe-noten" role="radiogroup" aria-label="Note">
      {grades.map((n) => {
        const active = value === n;
        const farbe = farbeFn ? farbeFn(n) : 'var(--ld-primary)';
        return (
          <button key={n} type="button" role="radio" onClick={() => onChange(n)} aria-checked={active}
            className={active ? 'ist-an' : undefined} style={active ? { background: farbe, borderColor: farbe } : undefined}>
            {n}
          </button>
        );
      })}
    </div>
  );
}

/** Umschalter (gleich breit). options=[{value,label}]. */
export function LdSegmented({ value, onChange, options }) {
  const schluessel = (v) => String(v);
  return (
    <LdUmschalter optionen={options.map((o) => ({ wert: schluessel(o.value), label: o.label }))} wert={schluessel(value)}
      onChange={(w) => { const o = options.find((x) => schluessel(x.value) === w); if (o) onChange(o.value); }} />
  );
}

/** Weißes Textarea-Feld. */
export function LdTextarea({ rows = 3, className, ...rest }) {
  return <textarea rows={rows} className={`ld-fe-input ld-fe-textarea${className ? ` ${className}` : ''}`} {...rest} />;
}

/** Hauptaktion über die volle Breite (für Fenster ohne Fußleiste). */
export function LdSheetSaveBar({ onClick, disabled = false, loading = false, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled || loading} className="ld-fe-knopf ld-fe-knopf--haupt" style={{ width: '100%', minHeight: 48 }}>
      {loading ? 'Speichern …' : children}
    </button>
  );
}
